import React, { Fragment, useState, useContext } from 'react';
import { getDates } from '../helper/helper_functions.js';
import { getTeam, getUserTeam } from '../../../util.js';
import Icon from '../../../components/atoms/Icon.js';
import Button from '../../../components/atoms/Button.js';
import ButtonGroup from '../../../components/molecules/ButtonGroup.js';
import { v4 as uuidv4 } from 'uuid';
import AuthContext from '../../../contexts/auth-context.js';
import moment from 'moment';
import AllStarLogo from '../../../assets/2025_all_star_logo.png';

const isGameSelected = (game, series, currentSeries, currentGame) => {
  return series?.teamID === currentSeries?.teamID && game.date === currentGame?.date;
};

const getSeasons = (games) => {
  const years = new Set();
  games.forEach(game => {
    game.dates.forEach(date => {
      years.add(moment(date.date).format('YYYY'));
    });
  });
  return Array.from(years).sort((a, b) => b - a);
};

const dropdownStyles = {
  '.dropdown-menu': {
    display: 'block',
    position: 'absolute',
    top: '100%',
    left: '0',
    transition: 'all 0.25s ease-out',
    transformOrigin: 'top',
    opacity: '0',
    transform: 'translateY(-10px)',
    pointerEvents: 'none',
    marginTop: '0',
    visibility: 'hidden'
  },
  '.dropdown-menu.show': {
    opacity: '1',
    transform: 'translateY(0)',
    pointerEvents: 'auto',
    visibility: 'visible'
  },
  '.caret-icon': {
    transition: 'transform 0.2s ease'
  },
  '.caret-icon.open': {
    transform: 'rotate(180deg)'
  },
  '.btn': {
    transition: 'background-color 0.2s ease, color 0.2s ease'
  },
  '.makeup': {
    color: '#A020F0' // Bootstrap yellow color
  },
  '.postponed-text, .makeup-text': {
    display: 'flex',
    alignItems: 'center',
  },
  '.makeup-text': {
    color: '#A020F0'
  }
};

const styleSheet = document.createElement('style');
styleSheet.textContent = Object.entries(dropdownStyles).map(([selector, rules]) => 
  `${selector} { ${Object.entries(rules).map(([prop, value]) => `${prop}: ${value};`).join(' ')} }`
).join('\n');
document.head.appendChild(styleSheet);

const GameSelector = ({
  loading,
  currentLocation,
  currentSeries,
  currentGame,
  currentSeason,
  setCurrentSeason,
  games,
  now,
  isToday,
  setCurrentGame,
  setCurrentSeries,
  setSelectedDate,
  theme
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const availableSeasons = getSeasons(games);
  const authCtx = useContext(AuthContext);

  const currentSeasonGames = games.filter((x) => 
    moment(x.dates[0].date).format("YYYY") === currentSeason
  );

  const handleSeasonChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentSeason(e.target.textContent);
  };

  const handleGameClick = (e, game, series) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentGame(game);
    setCurrentSeries(series);
    setSelectedDate(moment(game.date).toDate());
    setIsOpen(false);
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  // Check if there are any makeup games
  const hasMakeupGames = currentSeasonGames.some(series => 
    series.dates.some(date => date.makeup)
  );
  
  // Check if there are any postponed games
  const hasPostponedGames = currentSeasonGames.some(series => 
    series.dates.some(date => date.postponed)
  );

  return (
    <ButtonGroup className="between w-100 mt-3 panel-2">
      <div className={`btn-group dropdown w-${currentSeries ? "50" : "100"}`}>
        <button
          onClick={toggleDropdown}
          className={`btn between${loading ? " disabled" : ""}`}>
          {currentSeries ? (
            <span>
              <img
                alt="Opponent"
                className="me-1"
                width={20}
                height={20}
                style={{ borderRadius: "2px" }}
                src={
                 getTeam(currentLocation.league, currentSeries.teamID)?.imgURL
                }
                // src={
                //   currentSeries.teamID === 2025.0 && currentLocation.league === "mlb"
                //     ? AllStarLogo
                //     : getTeam(currentLocation.league, currentSeries.teamID)?.imgURL
                // }
              />
              <span>{getTeam(currentLocation.league, currentSeries.teamID)?.name}</span>
              {" @ "}
              <img
                alt="Home"
                className="me-1"
                width={20}
                height={20}
                src={getUserTeam(authCtx.league, authCtx.venue).imgURL}

              />
              <span className="me-1">{getUserTeam(authCtx.league, authCtx.venue).name}</span>
              {`(${moment(getDates(currentSeries.dates)[0]).format("MMM D")}`}
              {" - "}
              {`${moment(getDates(currentSeries.dates)[1]).format("MMM D")})`}
            </span>
          ) : (
            <span>Select A Game</span>
          )}
          <Icon 
            name="caret-down-fill" 
            className={`ms-2 caret-icon${isOpen ? ' open' : ''}`} 
          />
        </button>

        <div 
          // className={`dropdown-menu w-100 panel-2 border ${theme === "light" ? "border-black" : "border-white"}${isOpen ? ' show' : ''}`}
          className={`dropdown-menu w-100 panel-2 border border-black ${isOpen ? ' show' : ''}`}
          style={{
            maxHeight: '60vh',
            overflowY: 'auto'
          }}
        >
          {/* Updated Season Selector to match Overview's styling */}
          <div className="px-3 py-2">
            <div className="d-flex align-items-center">
              <span className="me-2" style={{ fontSize: "14px", fontWeight: "500" }}>Season:</span>
              <ButtonGroup className="season-tabs" size="sm">
                {availableSeasons.map((season) => (
                  <Button
                    key={season}
                    onClick={handleSeasonChange}
                    text={season}
                    className={currentSeason === season ? "active" : ""}
                    size="sm"
                  />
                ))}
              </ButtonGroup>
            </div>
          </div>

          {currentSeasonGames.map((series) => (
            <Fragment key={uuidv4()}>
              <div
                className={`px-3 py-2 between${moment(series.dates[0].date).isAfter(moment(now).hour() < 3 ? moment(now).subtract(1, 'days') : moment(now)) ? " opacity-25" : ""}`}>
                <div className="d-flex">
                  <div className="me-1 fw-bold">
                    <img
                      alt="Opponent"
                      className="me-2"
                      width={20}
                      height={20}
                      style={{ borderRadius: "2px" }}
                      src={
                       getTeam(currentLocation.league, series.teamID)?.imgURL
                      }
                      // src={
                      //   series.teamID === 2025.0 && currentLocation.league === "mlb"
                      //     ? AllStarLogo
                      //     : getTeam(currentLocation.league, series.teamID)?.imgURL
                      // }
                    />
                    <span>{getTeam(currentLocation.league, series.teamID)?.name}</span>
                  </div>
                  <span>
                    <span>
                      ({moment(getDates(series.dates)[0]).format("MMM D")}
                    </span>
                    {" - "}
                    <span>
                      {moment(getDates(series.dates)[1]).format("MMM D")})
                    </span>
                  </span>
                </div>
                <div className="d-flex">
                  {series.dates.map((game, idx) => (
                    <Fragment key={uuidv4()}>
                      {moment(game.date).isSameOrBefore(moment(now).hour() < 3 ? moment(now).subtract(1, 'days') : moment(now)) && (
                        <button
                          onClick={(e) => handleGameClick(e, game, series)}
                          className={`btn mx-1 py-0${
                            isGameSelected(game, series, currentSeries, currentGame) ? " active" : ""
                          }${game.postponed ? " postponed" : ""}${game.makeup ? " makeup" : ""}`}>
                          {idx + 1}
                          {game.postponed && (
                            <Icon name="exclamation-circle-fill ms-2" />
                          )}
                          {game.makeup && (
                            <Icon name="calendar4-week" className="ms-2" />
                          )}
                      </button>
                      )}
                    </Fragment>
                  ))}
                </div>
              </div>
            </Fragment>
          ))}
          
          {/* Legend for postponed and makeup games */}
          {(hasPostponedGames || hasMakeupGames) && (
            <div className="text-center p-2">
              <div style={{ display: 'inline-flex', gap: '20px' }}>
                {hasPostponedGames && (
                  <div className="small postponed-text">
                    <Icon name="exclamation-circle-fill" />
                    <span className="ms-1"> - Game was postponed</span>
                  </div>
                )}
                {hasMakeupGames && (
                  <div className="small makeup-text">
                    <Icon name="calendar4-week" />
                    <span className="ms-1"> - Makeup game</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {currentSeries && (
        <ButtonGroup className="w-50">
          {currentSeries.dates.map((game, idx) => (
            <Fragment key={uuidv4()}>
              {moment(game.date).isSameOrBefore(moment(now).hour() < 3 ? moment(now).subtract(1, 'days') : moment(now)) && (
                <Button
                  onClick={(e) => handleGameClick(e, game, currentSeries)}
                  className={`${currentGame === game ? "active" : ""}${loading ? " disabled" : ""}`}
                  text={`Game ${idx + 1} (${moment(currentSeries.dates[idx].date).format("ddd, MMM D")})`}
                />
              )}
            </Fragment>
          ))}
        </ButtonGroup>
      )}
    </ButtonGroup>
  );
};

export default GameSelector;